<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const FIELD_TYPES = [
  { id: 'text', name: 'Text' },
  { id: 'email', name: 'Email' },
  { id: 'text_area', name: 'Textarea' },
  { id: 'checkbox', name: 'Checkbox' },
  { id: 'info', name: 'Info block' },
  { id: 'link', name: 'Link' },
];

const VALUE_TYPES = [
  { id: '', name: 'Auto' },
  { id: 'boolean', name: 'Boolean' },
  { id: 'integer', name: 'Integer' },
  { id: 'string', name: 'String' },
];

const DEFAULT_FIELD = () => ({
  name: '',
  type: 'text',
  label: '',
  placeholder: '',
  required: false,
  pattern_error: '',
  pattern: '',
  map_to: '',
  value_type: '',
  title: '',
  url: '',
  default: '',
});

const DEFAULT_FORM = () => ({
  id: null,
  name: '',
  description: '',
  message: '',
  submit_label: '',
  active: true,
  fields: [DEFAULT_FIELD()],
  computed_fields_json: '[]',
  translations_json: '{}',
});

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const showEditorModal = ref(false);
const showDeleteModal = ref(false);
const editableForm = ref(DEFAULT_FORM());
const selectedForm = ref(null);

const records = computed(
  () => getters['automationForms/getAutomationForms'].value || []
);
const uiFlags = computed(
  () => getters['automationForms/getUIFlags'].value || {}
);
const isSaving = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const resetEditor = () => {
  editableForm.value = DEFAULT_FORM();
};

const loadFormForEdit = form => {
  editableForm.value = {
    id: form.id,
    name: form.name || '',
    description: form.description || '',
    message: form.message || '',
    submit_label: form.submit_label || '',
    active: form.active !== false,
    fields:
      Array.isArray(form.fields) && form.fields.length
        ? form.fields.map(field => ({
            ...DEFAULT_FIELD(),
            ...field,
          }))
        : [DEFAULT_FIELD()],
    computed_fields_json: JSON.stringify(form.computed_fields || [], null, 2),
    translations_json: JSON.stringify(form.translations || {}, null, 2),
  };
};

const openNewFormModal = () => {
  resetEditor();
  showEditorModal.value = true;
};

const openEditModal = form => {
  loadFormForEdit(form);
  showEditorModal.value = true;
};

const closeEditorModal = () => {
  showEditorModal.value = false;
  resetEditor();
};

const addField = () => {
  editableForm.value.fields.push(DEFAULT_FIELD());
};

const removeField = index => {
  if (editableForm.value.fields.length <= 1) {
    return;
  }

  editableForm.value.fields = editableForm.value.fields.filter(
    (_, fieldIndex) => fieldIndex !== index
  );
};

const parseJsonInput = (value, fallback) => {
  if (!value || !value.trim()) {
    return fallback;
  }

  return JSON.parse(value);
};

const normalizedFields = () => {
  return editableForm.value.fields.map(field => {
    const item = {
      name: field.name?.trim(),
      type: field.type,
      label: field.label,
      placeholder: field.placeholder,
      required: !!field.required,
      pattern_error: field.pattern_error,
      pattern: field.pattern,
      map_to: field.map_to,
      value_type: field.value_type,
      title: field.title,
      url: field.url,
      default: field.default,
    };

    if (['info', 'link'].includes(field.type)) {
      delete item.map_to;
      delete item.pattern;
      delete item.pattern_error;
      delete item.value_type;
      delete item.required;
    }

    if (field.type === 'info') {
      item.name =
        field.name?.trim() || `info_${Math.random().toString(36).slice(2, 8)}`;
    }

    if (field.type === 'link') {
      item.name =
        field.name?.trim() || `link_${Math.random().toString(36).slice(2, 8)}`;
    }

    return Object.fromEntries(
      Object.entries(item).filter(
        ([, value]) => value !== '' && value !== null && value !== undefined
      )
    );
  });
};

const saveForm = async () => {
  if (!editableForm.value.name?.trim()) {
    useAlert(t('AUTOMATION_FORMS.FORM.ERRORS.NAME_REQUIRED'));
    return;
  }

  if (!editableForm.value.fields.length) {
    useAlert(t('AUTOMATION_FORMS.FORM.ERRORS.FIELD_REQUIRED'));
    return;
  }

  let computedFields = [];
  let translations = {};

  try {
    computedFields = parseJsonInput(
      editableForm.value.computed_fields_json,
      []
    );
    translations = parseJsonInput(editableForm.value.translations_json, {});
  } catch (error) {
    useAlert(t('AUTOMATION_FORMS.FORM.ERRORS.INVALID_JSON'));
    return;
  }

  const payload = {
    automation_form: {
      name: editableForm.value.name.trim(),
      description: editableForm.value.description,
      message: editableForm.value.message,
      submit_label: editableForm.value.submit_label,
      active: editableForm.value.active,
      fields: normalizedFields(),
      computed_fields: computedFields,
      translations,
    },
  };

  try {
    if (editableForm.value.id) {
      await store.dispatch('automationForms/update', {
        id: editableForm.value.id,
        ...payload,
      });
      useAlert(t('AUTOMATION_FORMS.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch('automationForms/create', payload);
      useAlert(t('AUTOMATION_FORMS.ADD.API.SUCCESS_MESSAGE'));
    }
    closeEditorModal();
  } catch (error) {
    useAlert(
      editableForm.value.id
        ? t('AUTOMATION_FORMS.EDIT.API.ERROR_MESSAGE')
        : t('AUTOMATION_FORMS.ADD.API.ERROR_MESSAGE')
    );
  }
};

const openDeleteModal = form => {
  selectedForm.value = form;
  showDeleteModal.value = true;
};

const closeDeleteModal = () => {
  showDeleteModal.value = false;
  selectedForm.value = null;
};

const confirmDelete = async () => {
  if (!selectedForm.value?.id) {
    return;
  }

  try {
    await store.dispatch('automationForms/delete', selectedForm.value.id);
    useAlert(t('AUTOMATION_FORMS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AUTOMATION_FORMS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    closeDeleteModal();
  }
};

const formatTimestamp = timestamp => {
  if (!timestamp) {
    return '-';
  }

  return new Date(timestamp * 1000).toLocaleString();
};

onMounted(() => {
  store.dispatch('automationForms/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AUTOMATION_FORMS.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('AUTOMATION_FORMS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('AUTOMATION_FORMS.HEADER')"
        :description="$t('AUTOMATION_FORMS.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('AUTOMATION_FORMS.HEADER_BTN_TXT')"
            @click="openNewFormModal"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <table v-if="records.length" class="min-w-full divide-y divide-n-weak">
        <thead>
          <tr>
            <th class="py-3 text-left text-n-slate-11 font-semibold">
              {{ $t('AUTOMATION_FORMS.LIST.TABLE_HEADER.NAME') }}
            </th>
            <th class="py-3 text-left text-n-slate-11 font-semibold">
              {{ $t('AUTOMATION_FORMS.LIST.TABLE_HEADER.STATUS') }}
            </th>
            <th class="py-3 text-left text-n-slate-11 font-semibold">
              {{ $t('AUTOMATION_FORMS.LIST.TABLE_HEADER.UPDATED_AT') }}
            </th>
            <th class="py-3 text-right text-n-slate-11 font-semibold">
              {{ $t('AUTOMATION_FORMS.LIST.TABLE_HEADER.ACTIONS') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr v-for="form in records" :key="form.id">
            <td class="py-3">
              <p class="text-sm text-n-slate-12 font-medium">{{ form.name }}</p>
              <p v-if="form.description" class="text-xs text-n-slate-11">
                {{ form.description }}
              </p>
            </td>
            <td class="py-3 text-sm text-n-slate-11">
              {{
                form.active
                  ? $t('AUTOMATION_FORMS.STATUS.ACTIVE')
                  : $t('AUTOMATION_FORMS.STATUS.INACTIVE')
              }}
            </td>
            <td class="py-3 text-sm text-n-slate-11">
              {{ formatTimestamp(form.updated_at) }}
            </td>
            <td class="py-3">
              <div class="flex items-center justify-end gap-2">
                <Button
                  sm
                  slate
                  icon="i-lucide-pencil"
                  :label="$t('AUTOMATION_FORMS.ACTIONS.EDIT')"
                  @click="openEditModal(form)"
                />
                <Button
                  sm
                  ruby
                  icon="i-lucide-trash"
                  :label="$t('AUTOMATION_FORMS.ACTIONS.DELETE')"
                  @click="openDeleteModal(form)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </SettingsLayout>

  <woot-modal v-model:show="showEditorModal" :on-close="closeEditorModal">
    <div class="px-6 py-5 max-h-[85vh] overflow-y-auto">
      <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
        {{
          editableForm.id
            ? $t('AUTOMATION_FORMS.EDIT.TITLE')
            : $t('AUTOMATION_FORMS.ADD.TITLE')
        }}
      </h3>

      <div class="grid grid-cols-1 gap-3">
        <label class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION_FORMS.FORM.NAME') }}
          <input v-model="editableForm.name" class="mt-1 w-full" type="text" />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION_FORMS.FORM.DESCRIPTION') }}
          <textarea
            v-model="editableForm.description"
            class="mt-1 w-full"
            rows="2"
          />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION_FORMS.FORM.MESSAGE') }}
          <textarea
            v-model="editableForm.message"
            class="mt-1 w-full"
            rows="3"
          />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION_FORMS.FORM.SUBMIT_LABEL') }}
          <input
            v-model="editableForm.submit_label"
            class="mt-1 w-full"
            type="text"
          />
        </label>
        <label class="inline-flex items-center gap-2 text-sm text-n-slate-11">
          <input v-model="editableForm.active" type="checkbox" />
          {{ $t('AUTOMATION_FORMS.FORM.ACTIVE') }}
        </label>
      </div>

      <div class="mt-5">
        <div class="flex items-center justify-between mb-3">
          <h4 class="text-sm font-semibold text-n-slate-12">
            {{ $t('AUTOMATION_FORMS.FORM.FIELDS_TITLE') }}
          </h4>
          <Button
            sm
            blue
            faded
            icon="i-lucide-plus"
            :label="$t('AUTOMATION_FORMS.FORM.ADD_FIELD')"
            @click="addField"
          />
        </div>
        <div
          v-for="(field, index) in editableForm.fields"
          :key="`field-${index}`"
          class="mb-3 p-3 rounded-lg outline outline-1 outline-n-weak"
        >
          <div class="grid grid-cols-2 gap-3">
            <label class="text-xs text-n-slate-11">
              {{ $t('AUTOMATION_FORMS.FORM.FIELD_TYPE') }}
              <select v-model="field.type" class="mt-1 w-full">
                <option
                  v-for="fieldType in FIELD_TYPES"
                  :key="fieldType.id"
                  :value="fieldType.id"
                >
                  {{ fieldType.name }}
                </option>
              </select>
            </label>
            <label class="text-xs text-n-slate-11">
              {{ $t('AUTOMATION_FORMS.FORM.FIELD_NAME') }}
              <input v-model="field.name" class="mt-1 w-full" type="text" />
            </label>
            <label class="text-xs text-n-slate-11 col-span-2">
              {{ $t('AUTOMATION_FORMS.FORM.FIELD_LABEL') }}
              <input v-model="field.label" class="mt-1 w-full" type="text" />
            </label>

            <label
              v-if="!['checkbox', 'info', 'link'].includes(field.type)"
              class="text-xs text-n-slate-11 col-span-2"
            >
              {{ $t('AUTOMATION_FORMS.FORM.FIELD_PLACEHOLDER') }}
              <input
                v-model="field.placeholder"
                class="mt-1 w-full"
                type="text"
              />
            </label>

            <label
              v-if="field.type === 'link'"
              class="text-xs text-n-slate-11 col-span-2"
            >
              {{ $t('AUTOMATION_FORMS.FORM.FIELD_URL') }}
              <input v-model="field.url" class="mt-1 w-full" type="url" />
            </label>

            <label
              v-if="!['info', 'link'].includes(field.type)"
              class="text-xs text-n-slate-11 col-span-2"
            >
              {{ $t('AUTOMATION_FORMS.FORM.FIELD_MAP_TO') }}
              <input v-model="field.map_to" class="mt-1 w-full" type="text" />
            </label>

            <label
              v-if="['text', 'email', 'text_area'].includes(field.type)"
              class="text-xs text-n-slate-11"
            >
              {{ $t('AUTOMATION_FORMS.FORM.FIELD_PATTERN') }}
              <input v-model="field.pattern" class="mt-1 w-full" type="text" />
            </label>

            <label
              v-if="
                ['text', 'email', 'text_area', 'checkbox'].includes(field.type)
              "
              class="text-xs text-n-slate-11"
            >
              {{ $t('AUTOMATION_FORMS.FORM.FIELD_ERROR') }}
              <input
                v-model="field.pattern_error"
                class="mt-1 w-full"
                type="text"
              />
            </label>

            <label
              v-if="!['info', 'link'].includes(field.type)"
              class="text-xs text-n-slate-11"
            >
              {{ $t('AUTOMATION_FORMS.FORM.FIELD_VALUE_TYPE') }}
              <select v-model="field.value_type" class="mt-1 w-full">
                <option
                  v-for="valueType in VALUE_TYPES"
                  :key="valueType.id"
                  :value="valueType.id"
                >
                  {{ valueType.name }}
                </option>
              </select>
            </label>

            <label
              v-if="!['info', 'link'].includes(field.type)"
              class="inline-flex items-center gap-2 text-xs text-n-slate-11"
            >
              <input v-model="field.required" type="checkbox" />
              {{ $t('AUTOMATION_FORMS.FORM.FIELD_REQUIRED') }}
            </label>
          </div>
          <div class="mt-3 flex justify-end">
            <Button
              sm
              slate
              icon="i-lucide-trash"
              :label="$t('AUTOMATION_FORMS.FORM.REMOVE_FIELD')"
              @click="removeField(index)"
            />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-3 mt-3">
        <label class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION_FORMS.FORM.COMPUTED_FIELDS') }}
          <textarea
            v-model="editableForm.computed_fields_json"
            class="mt-1 w-full font-mono text-xs"
            rows="5"
          />
        </label>
        <label class="text-sm text-n-slate-11">
          {{ $t('AUTOMATION_FORMS.FORM.TRANSLATIONS') }}
          <textarea
            v-model="editableForm.translations_json"
            class="mt-1 w-full font-mono text-xs"
            rows="6"
          />
        </label>
      </div>

      <div class="mt-5 flex justify-end gap-2">
        <Button
          slate
          :label="$t('AUTOMATION_FORMS.FORM.CANCEL')"
          @click="closeEditorModal"
        />
        <Button
          blue
          :is-loading="isSaving"
          :label="$t('AUTOMATION_FORMS.FORM.SAVE')"
          @click="saveForm"
        />
      </div>
    </div>
  </woot-modal>

  <woot-delete-modal
    v-model:show="showDeleteModal"
    :on-close="closeDeleteModal"
    :on-confirm="confirmDelete"
    :title="$t('AUTOMATION_FORMS.DELETE.CONFIRM.TITLE')"
    :message="$t('AUTOMATION_FORMS.DELETE.CONFIRM.MESSAGE')"
    :message-value="selectedForm?.name ? ` ${selectedForm.name}` : ''"
    :confirm-text="$t('AUTOMATION_FORMS.DELETE.CONFIRM.YES')"
    :reject-text="$t('AUTOMATION_FORMS.DELETE.CONFIRM.NO')"
  />
</template>
